import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { createApiRequest } from '../../utils/apiUtils';
import { Box, Paper, Typography, Chip, Alert, CircularProgress, Stack, Button } from '@mui/material';

/** GET /stores/{storeId}/stripe のレスポンス。未連携時は connected=false。available=false の環境では機能自体が無効。 */
interface StripeConnection {
  available: boolean;
  connected: boolean;
  chargesEnabled: boolean;
  livemode: boolean;
}

/** GET /stores/{storeId}/stripe/connect のレスポンス。 */
interface StripeConnectStart {
  authorizeUrl: string;
}

const extractMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '処理に失敗しました。';
};

/**
 * Stripe Connect 連携セクション。予約の事前決済で店舗が自身の Stripe アカウントを接続する。
 * BE: GET/DELETE /stores/{storeId}/stripe、GET /stores/{storeId}/stripe/connect（MANAGER 認証）。
 * 連携ボタンは authorizeUrl（Stripe OAuth 認可画面）へ遷移し、コールバックは固定パス
 * /stripe/connect/callback（StripeConnectCallback）で受けて BE へ中継する。feast-fe ADR 0039。
 */
const StripeConnectionSection: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const [conn, setConn] = useState<StripeConnection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchStatus = useCallback(() => {
    if (!store_id) return Promise.resolve();
    return createApiRequest<StripeConnection>(`/stores/${store_id}/stripe`, { method: 'GET' })
      .then((data) => setConn(data))
      .catch((e) => setError(extractMessage(e)));
  }, [store_id]);

  useEffect(() => {
    setLoading(true);
    fetchStatus().finally(() => setLoading(false));
  }, [fetchStatus]);

  // 連携開始: BE から Stripe OAuth 認可画面の URL を取得してそのまま遷移する。
  // state は BE が署名付きで生成し、コールバック時に BE が検証する。
  const handleConnect = useCallback(() => {
    if (!store_id) return;
    setActionError(null);
    setBusy(true);
    createApiRequest<StripeConnectStart>(`/stores/${store_id}/stripe/connect`, { method: 'GET' })
      .then((data) => {
        window.location.assign(data.authorizeUrl);
        // 遷移するため busy は解除しない（二重クリック防止）。
      })
      .catch((e) => {
        setActionError(extractMessage(e));
        setBusy(false);
      });
  }, [store_id]);

  // 連携解除: 未処理の決済が残っている場合は BE が 409/422 で拒否するのでメッセージを表示する。
  const handleDisconnect = useCallback(() => {
    if (!store_id) return;
    if (!window.confirm('Stripe との連携を解除しますか？（予約の事前決済を受け取れなくなります）')) return;
    setActionError(null);
    setBusy(true);
    createApiRequest<void>(`/stores/${store_id}/stripe`, { method: 'DELETE' })
      .then(() => fetchStatus())
      .catch((e) => setActionError(extractMessage(e)))
      .finally(() => setBusy(false));
  }, [store_id, fetchStatus]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (!conn) {
    return error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null;
  }
  // 機能無効の環境（未設定 or 3DS対応まで封印中の本番）ではセクション自体を出さない。
  if (!conn.available) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">Stripe 連携（予約の事前決済）</Typography>
        <Chip
          size="small"
          color={conn.connected ? 'success' : 'default'}
          label={conn.connected ? '連携済み' : '未連携'}
        />
        {conn.connected && !conn.livemode && (
          <Chip size="small" color="warning" variant="outlined" label="テストモード" />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        予約の事前決済を受け取るには、店舗のStripeアカウントを連携してください。売上は店舗のStripeアカウントに直接入金されます。
      </Typography>

      {/* 連携済みでも Stripe 側の本人確認が完了するまで決済（charges）は有効化されない。 */}
      {conn.connected && !conn.chargesEnabled && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          本人確認未完了のため決済不可です。Stripe ダッシュボードで本人確認手続きを完了してください。
        </Alert>
      )}

      {actionError && <Alert severity="error" sx={{ mt: 1 }}>{actionError}</Alert>}

      {conn.connected ? (
        <Button size="small" variant="outlined" color="inherit" sx={{ mt: 1 }} disabled={busy} onClick={handleDisconnect}>
          連携を解除
        </Button>
      ) : (
        <Button size="small" variant="contained" sx={{ mt: 1 }} disabled={busy} onClick={handleConnect}>
          Stripeと連携する
        </Button>
      )}
    </Paper>
  );
};

export default StripeConnectionSection;
